import type { ReactNode } from 'react'
import type { Poll } from '../../types/polls'

const closingLine = (poll: Poll): string | null => {
  if (!poll.is_active) return 'Closed'
  if (!poll.closes_at) return null
  // Stored as UTC without a zone marker.
  const at = new Date(poll.closes_at.endsWith('Z') ? poll.closes_at : `${poll.closes_at}Z`)
  return `Closes ${at.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
}

interface Props {
  poll: Poll
  /** Called with the option picked; shown as buttons only when the viewer may vote. */
  onVote?: (optionIndex: number) => void
  voting?: boolean
  /** Why the viewer can't vote, when they can't (e.g. "Subscribers can vote"). */
  note?: string | null
  /** Beside the question, e.g. the creator's Close button. */
  action?: ReactNode
}

/**
 * One poll: the question, each option's share of the votes as a bar, and the
 * total. The counts are public; a viewer who may vote picks an option, and
 * the one they picked is marked afterwards.
 */
const PollCard = ({ poll, onVote, voting = false, note, action }: Props) => {
  const canVote = !!onVote && poll.viewer_can_vote === true
  const closing = closingLine(poll)

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4" data-testid="poll">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-gray-900">{poll.question}</p>
          <p className="mt-0.5 text-xs text-gray-500">
            {poll.total_votes} {poll.total_votes === 1 ? 'vote' : 'votes'}
            {poll.subscriber_only ? ' · subscribers vote' : ' · anyone signed in can vote'}
            {closing ? ` · ${closing}` : ''}
          </p>
        </div>
        {action}
      </div>

      <ul className="mt-3 space-y-1.5">
        {poll.results.map((r) => {
          const mine = poll.viewer_vote === r.index
          const label = (
            <>
              <span className="min-w-0 flex-1 truncate">
                {r.text}
                {mine && <span className="ml-1 text-xs font-medium text-primary-700">· your vote</span>}
              </span>
              <span className="shrink-0 tabular-nums text-gray-600">
                {r.vote_pct}% ({r.vote_count})
              </span>
            </>
          )
          return (
            <li key={r.index} className="relative overflow-hidden rounded-md bg-gray-50">
              <div
                aria-hidden="true"
                className={`absolute inset-y-0 left-0 ${mine ? 'bg-primary-100' : 'bg-gray-200'}`}
                style={{ width: `${r.vote_pct}%` }}
              />
              {canVote ? (
                <button
                  type="button"
                  onClick={() => onVote?.(r.index)}
                  disabled={voting}
                  className="relative flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-900 hover:bg-gray-100 disabled:opacity-50"
                >
                  {label}
                </button>
              ) : (
                <div className="relative flex items-center gap-2 px-3 py-1.5 text-sm text-gray-900">{label}</div>
              )}
            </li>
          )
        })}
      </ul>

      {canVote && <p className="mt-2 text-xs text-gray-500">Pick one to vote. A vote can&apos;t be changed.</p>}
      {!canVote && note && <p className="mt-2 text-xs text-gray-500">{note}</p>}
    </div>
  )
}

export default PollCard
